<template>
  <div>
    <!-- Filters -->
    <div class="el-filters">
      <div class="el-search">
        <div class="position-relative">
          <input
            type="text"
            class="form-control"
            v-model="search"
            placeholder="Buscar participante..."
            @input="debouncedLoad"
          />
          <i class="bx bx-search-alt el-search-icon"></i>
        </div>
      </div>
      <select class="form-control el-select" v-model="statusFilter" @change="loadPage(1)">
        <option value="">Todos os status</option>
        <option value="completed">Completado</option>
        <option value="active">Ativo</option>
        <option value="failed">Falhou</option>
        <option value="waiting">Aguardando</option>
      </select>
      <select class="form-control el-select" v-model="perPage" @change="loadPage(1)">
        <option :value="20">20 por pagina</option>
        <option :value="50">50 por pagina</option>
        <option :value="100">100 por pagina</option>
      </select>
    </div>

    <!-- Table -->
    <div class="el-table-wrap">
      <table class="el-table">
        <thead>
          <tr>
            <th>Participante</th>
            <th>Passo Atual</th>
            <th>Status</th>
            <th>Entrada</th>
            <th>Ultima Acao</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!logData.data || logData.data.length === 0">
            <td colspan="5" class="el-empty-row">
              <i class="bx bx-list-ul"></i>
              <span>Nenhum log encontrado</span>
            </td>
          </tr>
          <tr v-for="log in logData.data" :key="log.id" class="el-row el-row-clickable" @click="openTimeline(log)">
            <td>
              <span class="el-name">{{ getParticipantName(log) }}</span>
              <span class="el-email">{{ getParticipantEmail(log) }}</span>
            </td>
            <td>
              <div class="el-step-cell" v-if="getNodeLabel(log)">
                <span class="el-step-num" :class="{ 'el-step-final': isLastNode(log), 'el-step-branch': isBranchNode(log) }">{{ getStepLabel(log) }}</span>
                <span class="el-node-badge">{{ getNodeLabel(log) }}</span>
              </div>
              <span v-else class="el-no-data">-</span>
            </td>
            <td>
              <span class="el-status" :class="'el-status-' + log.status">
                {{ statusLabel(log.status) }}
              </span>
            </td>
            <td>
              <span class="el-date">{{ formatDate(getEnteredAt(log)) }}</span>
            </td>
            <td>
              <span class="el-date">{{ formatDate(log.updated_at) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="el-pagination" v-if="logData.total > perPage">
      <small class="el-page-info">
        Mostrando {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, logData.total) }}
        de {{ logData.total }}
      </small>
      <b-pagination
        v-model="currentPage"
        :total-rows="logData.total"
        :per-page="perPage"
        size="sm"
        @change="loadPage"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    journeyId: { type: [Number, String], required: true },
    logs: { type: Object, default: () => ({ data: [], total: 0 }) },
    totalNodes: { type: Number, default: 0 }
  },
  data () {
    return {
      search: '',
      statusFilter: '',
      perPage: 20,
      currentPage: 1,
      debounceTimer: null
    }
  },
  computed: {
    logData () {
      return this.logs || { data: [], total: 0 }
    }
  },
  methods: {
    getParticipantName (log) {
      if (log.enrollment && log.enrollment.participant) {
        return log.enrollment.participant.name || '-'
      }
      return log.participant_name || '-'
    },
    getParticipantEmail (log) {
      if (log.enrollment && log.enrollment.participant) {
        return log.enrollment.participant.email || ''
      }
      return log.participant_email || ''
    },
    getNodeLabel (log) {
      if (log.node && log.node.label) {
        return log.node.label
      }
      return log.current_node_label || null
    },
    getStepLabel (log) {
      if (!log.node) return '?'
      if (log.node.kind === 'end_journey') return 'FINAL'
      if (log.node.parent_node_id) {
        return log.node.branch_type === 'yes' ? 'S' : 'N'
      }
      const pos = log.node.position
      if (pos === null || pos === undefined) return '?'
      return pos + 1
    },
    isLastNode (log) {
      return log.node && log.node.kind === 'end_journey'
    },
    isBranchNode (log) {
      return log.node && log.node.parent_node_id
    },
    getEnteredAt (log) {
      if (log.enrollment && log.enrollment.entered_at) {
        return log.enrollment.entered_at
      }
      return log.entered_at || null
    },
    loadPage (page) {
      this.currentPage = page
      this.$emit('load-logs', {
        page: this.currentPage,
        limit: this.perPage,
        search: this.search || undefined,
        status: this.statusFilter || undefined
      })
    },
    openTimeline (log) {
      if (log.enrollment && log.enrollment.id) {
        this.$emit('open-timeline', log.enrollment.id)
      }
    },
    debouncedLoad () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.loadPage(1)
      }, 400)
    },
    statusLabel (status) {
      const labels = {
        active: 'Ativo',
        completed: 'Completado',
        failed: 'Falhou',
        exited: 'Saiu',
        paused: 'Pausado',
        waiting: 'Aguardando'
      }
      return labels[status] || status
    },
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimer)
  }
}
</script>

<style scoped>
/* ── Filters ── */
.el-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.el-search {
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}
.el-search-icon {
  position: absolute;
  right: 12px;
  top: 0;
  line-height: 38px;
  font-size: 16px;
  color: #9aa1b2;
}
.el-select {
  width: auto;
  min-width: 160px;
}
/* ── Table ── */
.el-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e5eb;
  border-radius: 5px;
}
.el-table {
  width: 100%;
  border-collapse: collapse;
}
.el-table thead {
  background: #f8f9fb;
}
.el-table th {
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #5b6478;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid #e2e5eb;
  white-space: nowrap;
}
.el-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #15181e;
  border-bottom: 1px solid #eceef2;
  vertical-align: middle;
}
.el-row:last-child td {
  border-bottom: none;
}
.el-row:hover {
  background: #f8f9fb;
}
.el-row-clickable {
  cursor: pointer;
}
.el-row-clickable:hover {
  background: #f0f4ff;
}

/* Cell content */
.el-name {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #15181e;
}
.el-email {
  display: block;
  font-size: 12px;
  color: #9aa1b2;
  margin-top: 1px;
}
.el-step-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.el-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 5px;
  background: #f1f3f6;
  color: #5b6478;
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
  flex-shrink: 0;
}
.el-step-final {
  background: #ecfdf5;
  color: #059669;
}
.el-step-branch {
  background: #eff6ff;
  color: #3b82f6;
  font-size: 10px;
}
.el-node-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  color: #3b82f6;
  background: #eff6ff;
  padding: 3px 10px;
  border-radius: 5px;
}
.el-no-data {
  color: #9aa1b2;
}
.el-date {
  font-size: 12.5px;
  color: #5b6478;
}

/* Status badges */
.el-status {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.el-status-active { background: #eff6ff; color: #3b82f6; }
.el-status-completed { background: #ecfdf5; color: #059669; }
.el-status-failed { background: #fef2f2; color: #ef4444; }
.el-status-exited { background: #fffbeb; color: #d97706; }
.el-status-paused { background: #f0f9ff; color: #0891b2; }
.el-status-waiting { background: #f1f3f6; color: #5b6478; }

/* Empty row */
.el-empty-row {
  text-align: center;
  padding: 40px 16px !important;
  color: #9aa1b2;
}
.el-empty-row i {
  font-size: 24px;
  display: block;
  margin-bottom: 6px;
}
.el-empty-row span {
  font-size: 13px;
}

/* ── Pagination ── */
.el-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}
.el-page-info {
  font-size: 12px;
  color: #9aa1b2;
}
</style>
